import React, { useState } from "react";
import { PlayIcon } from "./icons";

/**
 * One video card. Shows a lightweight YouTube thumbnail (lazy-loaded) until
 * the user clicks — only THEN do we mount the iframe. This avoids the page
 * loading 38+ YouTube iframes upfront.
 */
const VideoCard = ({ title, youtubeId, thumbnail }) => {
  const [playing, setPlaying] = useState(false);

  // YouTube thumbnail cascade. We use `hqdefault` as the primary because it's
  // the only size that's reliably populated for every video (480×360).
  // `maxresdefault` is sharper but 404s/serves a 1px placeholder on older or
  // shorter videos. If a custom `thumbnail` is provided (e.g. Norman, Renée,
  // Jully) we honor it as-is and skip the YouTube cascade entirely.
  const primary  = thumbnail || `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
  const fallback = thumbnail || `https://i.ytimg.com/vi/${youtubeId}/0.jpg`;

  return (
    <div
      data-testid={`video-card-${youtubeId}`}
      className="bk-card rounded-2xl overflow-hidden group"
    >
      <div className="relative aspect-video bg-ink-700">
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full group/play"
            aria-label={`Play testimonial: ${title}`}
            data-testid={`video-play-${youtubeId}`}
          >
            <img
              src={primary}
              alt={title}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/play:scale-105"
              onLoad={(e) => {
                // YouTube returns 200 OK + a tiny 120×90 "video unavailable"
                // placeholder when a size doesn't exist. Detect via natural dims
                // and step back to a smaller, reliably-populated size.
                const img = e.currentTarget;
                if (img.naturalWidth > 200) return; // looks legit
                if (!img.dataset.fallback) {
                  img.dataset.fallback = "1";
                  img.src = fallback;
                }
              }}
              onError={(e) => {
                const img = e.currentTarget;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = "1";
                  img.src = fallback;
                }
              }}
            />
            {/* Lighter gradient — keeps bottom title legible without blacking
                out darker YouTube thumbnails. */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bk-shimmer flex items-center justify-center text-black shadow-2xl group-hover/play:scale-110 transition-transform">
                <PlayIcon className="w-6 h-6 md:w-7 md:h-7 ml-0.5" />
              </span>
            </div>
            <div className="absolute bottom-3 left-4 right-4 text-left">
              <span className="text-[10px] uppercase tracking-[0.28em] text-gold/90">YouTube</span>
              <div className="font-display text-sm md:text-base mt-0.5 text-white line-clamp-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">{title}</div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

const PAGE = 6;

export default function VideoTestimonials({ videos = [] }) {
  const [shown, setShown] = useState(PAGE);

  const visible = videos.slice(0, shown);
  const hasMore = shown < videos.length;

  return (
    <section
      id="videos"
      data-testid="video-testimonials"
      className="relative py-24 md:py-32 border-y border-white/5 bg-ink-900/40"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.28em] text-gold/80">Client Success Stories</span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl">
            Real Results <span className="bk-gold-text italic">on Video</span>
          </h2>
          <p className="mt-5 text-white/65 leading-relaxed">
            Hear directly from clients about their journey and transformation.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {visible.map((v) => (
            <VideoCard key={v.youtube_id} title={v.title} youtubeId={v.youtube_id} thumbnail={v.thumbnail} />
          ))}
        </div>

        {hasMore ? (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setShown((s) => s + PAGE)}
              data-testid="load-more-videos"
              className="text-xs uppercase tracking-[0.22em] font-semibold px-7 py-3.5 rounded-full border border-white/15 hover:border-gold hover:text-gold transition-colors text-white/80"
            >
              Load More Videos · {videos.length - shown} more
            </button>
          </div>
        ) : (
          videos.length > PAGE ? (
            <div className="mt-10 text-center text-xs uppercase tracking-[0.22em] text-white/45">
              Showing all {videos.length} testimonials
            </div>
          ) : null
        )}
      </div>
    </section>
  );
}
